import { Loader2Icon, PinIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

import type { ChatPinnedMessage } from './chat-header.interface'
import { ChatPinnedMessageItem } from './chat-pinned-message-item.atom'

interface ChatHeaderPinnedMessagesProps {
  pinnedMessageList: ChatPinnedMessage[]
  onPinnedMessageClick?: (messageId: string) => void
  isJumping?: boolean
  jumpingMessageId?: string | null
  className?: string
}

const buildTriggerLabel = (count: number): string => {
  if (count === 0) return 'No pinned messages'
  return `${count} pinned message${count !== 1 ? 's' : ''}`
}

const renderEmptyState = (): ReactNode => (
  <div className="chat-header-pinned-empty px-4 py-6 text-center text-sm text-muted-foreground">
    No pinned messages yet. Hover a message and click the pin icon.
  </div>
)

interface PinnedListProps {
  pinnedMessageList: ChatPinnedMessage[]
  jumpingMessageId: string | null
  onItemClick: (messageId: string) => void
}

const PinnedMessagesList: React.FC<PinnedListProps> = ({
  pinnedMessageList,
  jumpingMessageId,
  onItemClick,
}): ReactNode => (
  <ScrollArea className={cn(pinnedMessageList.length > 4 && 'h-[300px]')}>
    <div className="chat-header-pinned-list p-1">
      {pinnedMessageList.map((message) => (
        <ChatPinnedMessageItem
          key={message.id}
          message={message}
          isJumping={jumpingMessageId === message.id}
          onClick={(): void => onItemClick(message.id)}
        />
      ))}
    </div>
  </ScrollArea>
)

/**
 * Popover showing list of pinned messages in the chat.
 * Always visible in the toolbar — shows an empty state when no pins exist.
 * Clicking a message triggers the scroll-to-pinned flow.
 */
export const ChatHeaderPinnedMessages: React.FC<ChatHeaderPinnedMessagesProps> = ({
  pinnedMessageList,
  onPinnedMessageClick,
  isJumping = false,
  jumpingMessageId = null,
  className,
}): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)

  const handleItemClick = (messageId: string): void => {
    onPinnedMessageClick?.(messageId)
    setIsOpen(false)
  }

  const count = pinnedMessageList.length

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('chat-header-pinned-trigger gap-1.5 px-2', className)}
          aria-label={buildTriggerLabel(count)}
        >
          <PinIcon className="size-4" />
          {count > 0 && <span className="text-sm font-medium">{count}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="chat-header-pinned-popover w-80 p-0">
        <div className="chat-header-pinned-header flex items-center justify-between border-b px-3 py-2">
          <h4 className="text-sm font-semibold">Pinned Messages{count > 0 ? ` (${count})` : ''}</h4>
          {isJumping && <Loader2Icon className="size-4 animate-spin text-muted-foreground" />}
        </div>
        {count === 0 ? (
          renderEmptyState()
        ) : (
          <PinnedMessagesList
            pinnedMessageList={pinnedMessageList}
            jumpingMessageId={jumpingMessageId}
            onItemClick={handleItemClick}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}

ChatHeaderPinnedMessages.displayName = 'ChatHeaderPinnedMessages'
